import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getStoreAnalytics,
  getRestockRequests,
  getStockTransfers,
  getLowStockItems,
} from "../../../redux/StoreManagement/storeManagementActions";
import PaginationComponentOne from "../../../components/Pagination/Pagination-O";
import EmptyState from "../../../components/EmptyState";

import {
  Schedule,
  Clear,
  Shuffle,
  DoneAll,
  Block,
  AddCircleOutline,
} from "@mui/icons-material";

import clock_loader_10 from "../../../assets/icons/clock_loader_10.svg";
import deployed_code_history from "../../../assets/icons/deployed_code_history.svg";
import order_icon from "../../../assets/icons/orders.svg";
import inventory_icon from "../../../assets/icons/inventory.svg";
import deployed_code_alert from "../../../assets/icons/deployed_code_alert.svg";
import box_add from "../../../assets/icons/box_add.svg";

const StoreInsights = () => {
  const dispatch = useDispatch();
  const {
    storeAnalyticsData,
    storeAnalyticsLoading,
    storeAnalyticsError,
    storeAnalyticsTotalCount,
    storeAnalyticsCurrentPage,
    restockRequestsData,
    restockRequestsLoading,
    restockRequestsError,
    restockRequestsTotalCount,
    restockRequestsCurrentPage,
    stockTransfersData,
    stockTransfersLoading,
    stockTransfersError,
    stockTransfersTotalCount,
    stockTransfersCurrentPage,
    lowStockData,
    lowStockLoading,
    lowStockError,
    lowStockTotalCount,
    lowStockCurrentPage,
  } = useSelector((state) => state.storeManagement);

  const [activeTab, setActiveTab] = useState("topStores");
  const [timeframe, setTimeframe] = useState("thisWeek");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const getStatusIcon = (status) => {
    switch (status) {
      case "APPROVED":
        return (
          <DoneAll
            className="text-[#2B8C34]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "OVER_APPROVED":
        return (
          <AddCircleOutline
            className="text-[#0F766E]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "PROCESSING":
        return (
          <img
            src={deployed_code_history}
            alt="Processing Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "DISPATCHED":
        return (
          <Shuffle
            className="text-[#0066AF]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "CONFIRMED":
        return (
          <DoneAll
            className="text-[#2B8C34]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "FULFILLED":
        return (
          <img
            src={inventory_icon}
            alt="Fulfilled Icon"
            className="text-[#2B8C34]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "CANCELLED":
        return (
          <Clear
            className="text-[#B92020]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "PENDING":
      case "PENDING_APPROVAL":
        return (
          <Schedule
            className="text-[#D4A407]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "PART_FULFILLED":
      case "PART_DELIVERY":
        return (
          <img
            src={clock_loader_10}
            alt="Part Fulfilled Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "REJECTED":
        return (
          <Block
            className="text-[#B92020]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "ISSUE_REPORTED":
        return (
          <img
            src={deployed_code_alert}
            alt="Issue Reported Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "OVER_FULFILLED":
        return (
          <img
            src={box_add}
            alt="Over Fulfilled Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
      default:
        return (
          <img
            src={order_icon}
            alt="Order Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case "":
      case "--":
      case null:
      case undefined:
        return "No Item";
      case "APPROVED":
        return "Approved";
      case "OVER_APPROVED":
        return "Over Approved";
      case "PROCESSING":
        return "Processing";
      case "DISPATCHED":
        return "Dispatched";
      case "CONFIRMED":
        return "Confirmed";
      case "FULFILLED":
        return "Fulfilled";
      case "CANCELLED":
        return "Cancelled";
      case "PENDING":
        return "Pending";
      case "PENDING_APPROVAL":
        return "Pending Approval";
      case "PART_FULFILLED":
        return "Part Fulfilled";
      case "PART_DELIVERY":
        return "Part Delivery";
      case "REJECTED":
        return "Rejected";
      case "ISSUE_REPORTED":
        return "Issue Reported";
      case "OVER_FULFILLED":
        return "Over Fulfilled";
      default:
        return status
          ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
          : "No Item";
    }
  };

  // Status badge styles - aligned with StockRequestsTable
  const getStatusBadgeStyles = (status) => {
    switch (status) {
      case "APPROVED":
        return "bg-[#FFFFFF] text-[#24752B] border border-[#2B8C34]";
      case "OVER_APPROVED":
        return "bg-[#FFFFFF] text-[#0F766E] border border-[#0F766E]";
      case "PROCESSING":
        return "bg-[#FFFCC5] text-[#E29700] border border-[#FFC508]";
      case "DISPATCHED":
        return "bg-[#E7FBFE] text-[#0066AF] border border-[#38AFE0]";
      case "CONFIRMED":
        return "bg-[#FFFFFF] text-[#2B8C34] border border-[#2B8C34]";
      case "FULFILLED":
        return "bg-[#E1F7E3] text-[#24752B] border border-[#72B278]";
      case "CANCELLED":
        return "bg-[#FEF0EC] text-[#B92020] border border-[#E75E63]";
      case "PENDING":
      case "PENDING_APPROVAL":
        return "bg-[#FFFFFF] text-[#E29700] border border-[#E29700]";
      case "PART_FULFILLED":
      case "PART_DELIVERY":
        return "bg-[#FFEDD5] text-[#C2410C] border border-[#F97316]";
      case "REJECTED":
        return "bg-[#FEF0EC] text-[#B92020] border border-[#E75E63]";
      case "ISSUE_REPORTED":
        return "bg-[#FEF3C7] text-[#B45309] border border-[#F59E0B]";
      case "OVER_FULFILLED":
        return "bg-[#CCFBF1] text-[#0F766E] border border-[#14B8A6]";
      default:
        return "bg-[#ECEEEC] text-[#96A397] border border-[#96A397]";
    }
  };

  // Enhanced Status Badge Component
  const StatusBadge = ({ status }) => (
    <span
      className={`px-2 py-1 rounded-[4px] text-xs font-semibold inline-flex items-center gap-1 whitespace-nowrap ${getStatusBadgeStyles(
        status
      )}`}
    >
      {getStatusIcon(status)}
      {formatStatus(status)}
    </span>
  );

  // Helper function to get date ranges based on timeframe
  const getDateRange = (timeframe) => {
    // Use current date directly to avoid timezone issues
    const now = new Date();

    switch (timeframe) {
      case "thisWeek": {
        const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        console.log("This week calculation:", {
          today: now.toISOString().split("T")[0],
          dayOfWeek,
        });

        // Create start of week (Sunday) by going back the correct number of days
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - dayOfWeek);
        console.log("Start of week:", startOfWeek.toISOString().split("T")[0]);

        // Create end of week (Saturday) by adding 6 days to start of week
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        console.log("End of week:", endOfWeek.toISOString().split("T")[0]);

        return {
          from_date: startOfWeek.toISOString().split("T")[0],
          to_date: endOfWeek.toISOString().split("T")[0],
        };
      }
      case "lastWeek": {
        const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        // Create start of last week (previous Sunday)
        const startOfLastWeek = new Date(now);
        startOfLastWeek.setDate(now.getDate() - dayOfWeek - 7);

        // Create end of last week (previous Saturday)
        const endOfLastWeek = new Date(startOfLastWeek);
        endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);

        return {
          from_date: startOfLastWeek.toISOString().split("T")[0],
          to_date: endOfLastWeek.toISOString().split("T")[0],
        };
      }
      case "thisMonth": {
        // Get first day of current month
        const startOfMonth = new Date(now);
        startOfMonth.setDate(1); // Set to 1st day of current month

        // Get last day of current month
        const endOfMonth = new Date(now);
        endOfMonth.setMonth(now.getMonth() + 1); // Go to next month
        endOfMonth.setDate(0); // Set to 0th day = last day of previous month (current month)

        console.log("This month calculation:", {
          currentMonth: now.getMonth() + 1, // +1 because getMonth() is 0-based
          year: now.getFullYear(),
          startOfMonth: startOfMonth.toISOString().split("T")[0],
          endOfMonth: endOfMonth.toISOString().split("T")[0],
        });

        return {
          from_date: startOfMonth.toISOString().split("T")[0],
          to_date: endOfMonth.toISOString().split("T")[0],
        };
      }
      case "lastMonth": {
        // Get first day of last month
        const startOfLastMonth = new Date(now);
        startOfLastMonth.setMonth(now.getMonth() - 1); // Go to previous month
        startOfLastMonth.setDate(1); // Set to 1st day of that month

        // Get last day of last month
        const endOfLastMonth = new Date(now);
        endOfLastMonth.setDate(0); // Set to 0th day = last day of previous month

        console.log("Last month calculation:", {
          lastMonth: now.getMonth(), // This shows current month number (0-based)
          year: now.getFullYear(),
          startOfLastMonth: startOfLastMonth.toISOString().split("T")[0],
          endOfLastMonth: endOfLastMonth.toISOString().split("T")[0],
        });

        return {
          from_date: startOfLastMonth.toISOString().split("T")[0],
          to_date: endOfLastMonth.toISOString().split("T")[0],
        };
      }
      default:
        return {
          from_date: now.toISOString().split("T")[0],
          to_date: now.toISOString().split("T")[0],
        };
    }
  };

  // Fetch store analytics when component mounts or dependencies change
  useEffect(() => {
    const dateRange = getDateRange(timeframe);
    console.log("Date range calculation:", {
      timeframe,
      today: new Date().toISOString().split("T")[0],
      dayOfWeek: new Date().getDay(),
      dateRange,
    });

    if (activeTab === "topStores") {
      dispatch(
        getStoreAnalytics({
          page,
          page_size: pageSize,
          ...dateRange,
        })
      );
    } else if (activeTab === "restockRequests") {
      dispatch(
        getRestockRequests({
          page,
          page_size: pageSize,
          ...dateRange,
        })
      );
    } else if (activeTab === "stockTransfers") {
      dispatch(
        getStockTransfers({
          page,
          page_size: pageSize,
          ...dateRange,
        })
      );
    } else if (activeTab === "lowStock") {
      dispatch(
        getLowStockItems({
          page,
          page_size: pageSize,
          ...dateRange,
        })
      );
    }
  }, [dispatch, activeTab, timeframe, page, pageSize]);

  // Handle timeframe change
  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    setPage(1); // Reset to first page when changing timeframe
  };

  const handlePageChange = (paginationData) => {
    if (typeof paginationData === "object") {
      if (paginationData.page !== undefined) {
        setPage(paginationData.page);
      }
      if (
        paginationData.pageSize !== undefined &&
        paginationData.pageSize !== pageSize
      ) {
        setPageSize(paginationData.pageSize);
        setPage(1);
      }
    } else {
      setPage(paginationData);
    }
  };

  const renderTopStoresTable = () => {
    if (storeAnalyticsLoading) {
      return (
        <div className="flex justify-center items-center h-[200px]">
          <p className="text-[#5F6D60] text-sm">Loading store analytics...</p>
        </div>
      );
    }

    if (storeAnalyticsError) {
      return (
        <div className="flex justify-center items-center h-[200px]">
          <p className="text-[#B92020] text-sm">Error: {storeAnalyticsError}</p>
        </div>
      );
    }

    if (!storeAnalyticsData || storeAnalyticsData.length === 0) {
      return (
        <EmptyState text="No store analytics data available for this timeframe" />
      );
    }

    return (
      <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1]">
        <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
          <tr className="text-sm">
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Store</th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Store manager
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Top product
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Products Sold
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Total sales
            </th>
          </tr>
        </thead>
        <tbody className="bg-white border-[#E1E6E1]">
          {storeAnalyticsData.map((store) => (
            <tr
              key={store.id}
              className="text-sm hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
            >
              <td className="px-4 py-4 font-medium text-[#344335]">
                {store.storeName}
              </td>
              <td className="px-4 py-4 text-[#5F6D60]">{store.storeManager}</td>
              <td className="px-4 py-4 text-[#5F6D60]">{store.topProduct}</td>
              <td className="px-4 py-4 text-[#5F6D60]">
                {store.productsSold.toLocaleString()}
              </td>
              <td className="px-4 py-4 font-medium text-[#344335]">
                {store.totalSales}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderRestockRequestsTable = () => {
    if (restockRequestsLoading) {
      return (
        <div className="flex justify-center items-center h-[200px]">
          <p className="text-[#5F6D60] text-sm">Loading restock requests...</p>
        </div>
      );
    }

    if (restockRequestsError) {
      return (
        <div className="flex justify-center items-center h-[200px]">
          <p className="text-[#B92020] text-sm">
            Error: {restockRequestsError}
          </p>
        </div>
      );
    }

    if (!restockRequestsData || restockRequestsData.length === 0) {
      return (
        <EmptyState text="No restock requests available for this timeframe" />
      );
    }

    return (
      <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1]">
        <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
          <tr className="text-sm">
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Store</th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Product</th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Requested Qty
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Fulfilled Qty
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Request Date
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Status</th>
          </tr>
        </thead>
        <tbody className="bg-white border-[#E1E6E1]">
          {restockRequestsData.map((request) => (
            <tr
              key={request.id}
              className="text-sm hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
            >
              <td className="px-4 py-4 font-medium text-[#344335]">
                {request.storeName}
              </td>
              <td className="px-4 py-4 text-[#5F6D60]">{request.product}</td>
              <td className="px-4 py-4 text-[#5F6D60]">
                {request.requestedQty.toLocaleString()}
              </td>
              <td className="px-4 py-4 text-[#5F6D60]">
                {request.currentStock.toLocaleString()}
              </td>
              <td className="px-4 py-4 text-[#5F6D60]">
                {request.requestDate}
              </td>
              <td className="px-4 py-4">
                <StatusBadge status={request.approvalStatus} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderStockTransfersTable = () => {
    if (stockTransfersLoading) {
      return (
        <div className="flex justify-center items-center h-[200px]">
          <p className="text-[#5F6D60] text-sm">Loading stock transfers...</p>
        </div>
      );
    }

    if (stockTransfersError) {
      return (
        <div className="flex justify-center items-center h-[200px]">
          <p className="text-[#B92020] text-sm">Error: {stockTransfersError}</p>
        </div>
      );
    }

    if (!stockTransfersData || stockTransfersData.length === 0) {
      return (
        <EmptyState text="No stock transfers available for this timeframe" />
      );
    }

    return (
      <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1]">
        <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
          <tr className="text-sm">
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              From Store
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">To Store</th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Product</th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Quantity</th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Transfer Date
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Status</th>
          </tr>
        </thead>
        <tbody className="bg-white border-[#E1E6E1]">
          {stockTransfersData.map((transfer) => (
            <tr
              key={transfer.id}
              className="text-sm hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
            >
              <td className="px-4 py-4 font-medium text-[#344335]">
                {transfer.fromStore}
              </td>
              <td className="px-4 py-4 text-[#5F6D60]">{transfer.toStore}</td>
              <td className="px-4 py-4 text-[#5F6D60]">{transfer.product}</td>
              <td className="px-4 py-4 text-[#5F6D60]">
                {transfer.quantity.toLocaleString()}
              </td>
              <td className="px-4 py-4 text-[#5F6D60]">
                {transfer.transferDate}
              </td>
              <td className="px-4 py-4">
                <StatusBadge status={transfer.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderLowStockTable = () => {
    if (lowStockLoading) {
      return (
        <div className="flex justify-center items-center h-[200px]">
          <p className="text-[#5F6D60] text-sm">Loading low stock items...</p>
        </div>
      );
    }

    if (lowStockError) {
      return (
        <div className="flex justify-center items-center h-[200px]">
          <p className="text-[#B92020] text-sm">Error: {lowStockError}</p>
        </div>
      );
    }

    if (!lowStockData || lowStockData.length === 0) {
      return (
        <EmptyState text="No low stock items available for this timeframe" />
      );
    }

    return (
      <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1]">
        <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
          <tr className="text-sm">
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Store</th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Product</th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">Variant</th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Current Stock
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Minimum Stock
            </th>
            <th className="px-4 py-4 font-semibold text-[#5F6D60]">
              Last Updated
            </th>
          </tr>
        </thead>
        <tbody className="bg-white border-[#E1E6E1]">
          {lowStockData.map((item) => (
            <tr
              key={item.id}
              className="text-sm hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
            >
              <td className="px-4 py-4 font-medium text-[#344335]">
                {item.storeName}
              </td>
              <td className="px-4 py-4 text-[#5F6D60]">{item.product}</td>
              <td className="px-4 py-4 text-[#5F6D60] text-xs">
                {item.variantDisplay}
              </td>
              <td className="px-4 py-4 text-[#B92020] font-medium">
                {item.currentStock}
              </td>
              <td className="px-4 py-4 text-[#5F6D60]">{item.minimumStock}</td>
              <td className="px-4 py-4 text-[#5F6D60]">{item.lastUpdated}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const getCurrentTotalCount = () => {
    switch (activeTab) {
      case "topStores":
        return storeAnalyticsTotalCount || 0;
      case "restockRequests":
        return restockRequestsTotalCount || 0;
      case "stockTransfers":
        return stockTransfersTotalCount || 0;
      case "lowStock":
        return lowStockTotalCount || 0;
      default:
        return 0;
    }
  };

  const getCurrentTable = () => {
    switch (activeTab) {
      case "topStores":
        return renderTopStoresTable();
      case "restockRequests":
        return renderRestockRequestsTable();
      case "stockTransfers":
        return renderStockTransfersTable();
      case "lowStock":
        return renderLowStockTable();
      default:
        return <EmptyState text="No data available" />;
    }
  };

  const totalCount = getCurrentTotalCount();

  return (
    <div className="mt-6 bg-white rounded-[8px] border border-[#E1E6E1] py-4 px-4">
      {/* Header with title and timeframe dropdown */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-[#344335]">Insights</h2>
        <select
          value={timeframe}
          onChange={(e) => handleTimeframeChange(e.target.value)}
          className="px-3 py-2 border border-[#E1E6E1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B8C34] text-sm text-[#5F6D60]"
        >
          <option value="thisWeek">This week</option>
          <option value="lastWeek">Last week</option>
          <option value="thisMonth">This month</option>
          <option value="lastMonth">Last month</option>
        </select>
      </div>

      {/* Tab Navigation */}
      <div className="pb-3 w-full border-b border-[#E1E6E1]">
        <div className="text-sm font-semibold text-[#3E473F] border border-[#E1E6E1] mt-3 max-w-[800px] rounded-lg overflow-hidden flex">
          <button
            className={`px-3 py-2 text-center flex-1 ${
              activeTab === "topStores" ? "bg-white rounded-lg" : "bg-[#F6F7F6]"
            }`}
            onClick={() => setActiveTab("topStores")}
          >
            Top Stores
          </button>
          <button
            className={`px-3 py-2 text-center flex-1 ${
              activeTab === "restockRequests"
                ? "bg-white rounded-lg"
                : "bg-[#F6F7F6]"
            }`}
            onClick={() => setActiveTab("restockRequests")}
          >
            Restock Request
          </button>
          <button
            className={`px-3 py-2 text-center flex-1 ${
              activeTab === "stockTransfers"
                ? "bg-white rounded-lg"
                : "bg-[#F6F7F6]"
            }`}
            onClick={() => setActiveTab("stockTransfers")}
          >
            Stock transfer
          </button>
          <button
            className={`px-3 py-2 text-center flex-1 ${
              activeTab === "lowStock" ? "bg-white rounded-lg" : "bg-[#F6F7F6]"
            }`}
            onClick={() => setActiveTab("lowStock")}
          >
            Low Stock
          </button>
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto">{getCurrentTable()}</div>

      {/* Pagination - Show only if there's data and more than one page */}
      {totalCount > 0 && (
        <div className="border-t border-[#E1E6E1] mt-4">
          <PaginationComponentOne
            totalItems={totalCount}
            page={
              activeTab === "topStores"
                ? storeAnalyticsCurrentPage
                : activeTab === "restockRequests"
                ? restockRequestsCurrentPage
                : activeTab === "stockTransfers"
                ? stockTransfersCurrentPage
                : activeTab === "lowStock"
                ? lowStockCurrentPage
                : page
            }
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default StoreInsights;
